// src/contexts/UserContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface UserInfo {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  gender: string;
  date_of_birth: string | null;
  city?: string;
  industry?: string;
  is_open_for_work?: boolean;
  is_currently_employed?: boolean;
  experience?: string;
  other_software?: string;
  linkedin_profile?: string;
  is_admin: boolean;
  is_super_admin: boolean;
  user_type: 'standard' | 'admin' | 'super_admin';
  share_profile: boolean;
  allow_contact: boolean;
  profile_image?: string;
  has_self_purchased_tests?: boolean;
  is_external_api_user?: boolean;
}

interface UserContextType {
  user: UserInfo | null
  loading: boolean
  fetchUserInfo: () => Promise<void>
  logout: () => Promise<void>
  isAdmin: () => boolean
  isSuperAdmin: () => boolean
  isStandardUser: () => boolean
  isExternalApiUser: () => boolean
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const useUser = () => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

interface UserProviderProps {
  children: ReactNode
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchUserInfo = async () => {
    const token = localStorage.getItem('testinvest_token')
    if (!token) {
      setUser(null)
      setLoading(false)
      return
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/info/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUser(data)
      } else {
        setUser(null)
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    const token = localStorage.getItem('testinvest_token')
    if (token) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/api/logout/`, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
          },
        })
      } catch (error) {
        console.error('Logout API error:', error)
      }
    }
    localStorage.removeItem('testinvest_token')
    setUser(null)
  }

  useEffect(() => {
    fetchUserInfo()

    // Monitor localStorage changes (for when user logs in from another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'testinvest_token') {
        fetchUserInfo()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const isAdmin = (): boolean => user?.is_admin || false
  const isSuperAdmin = (): boolean => user?.is_super_admin || false
  const isStandardUser = (): boolean => Boolean(user && !user.is_admin && !user.is_super_admin)
  const isExternalApiUser = (): boolean => user?.is_external_api_user || false

  const value: UserContextType = {
    user,
    loading,
    fetchUserInfo,
    logout,
    isAdmin,
    isSuperAdmin,
    isStandardUser,
    isExternalApiUser
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}